import { FC, useCallback, useMemo, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { useSelector } from 'store';
import { useAddCorrespondenceMutation, useUpdateCorrespondenceMutation } from 'store/api/correspondence';
import { ClassifierData } from 'types/classifiers';
import { CorrespondenceForm } from 'types/correspondence';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useGoBack, useLeaveForm } from 'utils/useNavigateWithReturnState';
import { correspondenceFormToRequest } from './correspondenceFormMapping';
import {
    ConnectedClientCaseFields,
    DmsFields,
    GeneralFields,
    HandlingFields,
    NoteFields,
    OfficialFields,
    SendersFields,
} from './form-sections/CorrespondenceFields';
import { useCorrespondenceForm } from './form-sections/useCorrespondenceForm';

type Props = {
    type: 'add' | 'edit';
    initialData?: CorrespondenceForm;
    classifiers: Record<string, ClassifierData>;
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    readOnlyNumber: 'correspondence.general',
    name: 'correspondence.general',
    typeCode: 'correspondence.general',
    directionTypeCode: 'correspondence.general',
    categoryTypeCode: 'correspondence.general',
    date: 'correspondence.general',
    documentDate: 'correspondence.general',
    isOfficial: 'correspondence.general',
    officialReference: 'correspondence.general',
    employees: 'correspondence.general',
    sendersOrRecipients: 'correspondence.general',
    note: 'correspondence.general',
    isHandled: 'correspondence.general',
    statusTypeCode: 'correspondence.general',
    clients: 'correspondence.connected-client-case',
    matters: 'correspondence.connected-client-case',
    assignments: 'correspondence.connected-client-case',
    dmsLink: 'correspondence.dms',
};

const CorrespondencePageForm: FC<Props> = ({ type = 'add', initialData, classifiers }) => {
    useFocusedEditMode('correspondence');

    const { id: correspondenceId } = useCreateRoute().params;
    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const returnTo = correspondenceId ? `/correspondence/${correspondenceId}` : '/correspondence';
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const [addCorrespondence] = useAddCorrespondenceMutation();
    const [updateCorrespondence] = useUpdateCorrespondenceMutation();

    const form = useCorrespondenceForm({ type, initialData, classifiers });
    const { methods } = form;
    useCreateDialogForm(methods);
    const { handleSubmit, control, reset } = methods;

    const { errors } = methods.formState;
    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);

    const bypassNavigationBlockerRef = useRef(false);

    const onSubmit = useCallback(
        async (formData: CorrespondenceForm, action: 'create' | 'update') => {
            try {
                const data = correspondenceFormToRequest(formData);
                if (action === 'create') {
                    await addCorrespondence({ ...data })
                        .unwrap()
                        .then((resp) => {
                            bypassNavigationBlockerRef.current = true;
                            reset({}, { keepValues: true });
                            leaveForm(`/correspondence/${resp.id}`);
                        })
                        .catch((e) => console.error(e));
                } else if (action === 'update' && correspondenceId && !isNaN(Number(correspondenceId))) {
                    await updateCorrespondence({ ...data, id: correspondenceId })
                        .unwrap()
                        .then(() => {
                            bypassNavigationBlockerRef.current = true;
                            reset({}, { keepValues: true });
                            leaveForm(returnTo);
                        })
                        .catch((e) => console.error(e));
                }
            } catch (e) {
                console.error('onSubmit error:', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [correspondenceId, addCorrespondence, updateCorrespondence, leaveForm, reset, returnTo]
    );

    const submitCreateCorrespondence = useCallback(() => {
        handleSubmit((data) => onSubmit(data, 'create'))();
    }, [handleSubmit, onSubmit]);

    const submitUpdateCorrespondence = useCallback(() => {
        handleSubmit((data) => onSubmit(data, 'update'))();
    }, [handleSubmit, onSubmit]);

    const formTitle = useMemo(() => {
        switch (type) {
            case 'edit':
                return initialData?.name;
            default:
                return t('correspondence.add-new-correspondence');
        }
    }, [type, initialData]);

    const formActions = useMemo(() => {
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={goBack} />];

        if (type === 'add') {
            actions.push(
                <SaveButton
                    key="addSave"
                    permissions={['CREATE.CORRESPONDENCE']}
                    onClick={submitCreateCorrespondence}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit') {
            actions.push(
                <PermissionButton
                    key="editSave"
                    permissions={[
                        'UPDATE.CORRESPONDENCE.ALL',
                        'UPDATE.CORRESPONDENCE.MY_CASES',
                        'UPDATE.CORRESPONDENCE.CREATED_BY_ME',
                        'UPDATE.CORRESPONDENCE.MY_OWN',
                        'UPDATE.CORRESPONDENCE.MY_TEAM_CASES',
                    ]}
                    color="secondary"
                    variant="contained"
                    size="small"
                    onClick={submitUpdateCorrespondence}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="save" />
                </PermissionButton>
            );
        }
        return actions;
    }, [type, goBack, buttonsDisabled, submitCreateCorrespondence, submitUpdateCorrespondence]);

    const formHeader = useMemo(
        () => <ActionHeader title={formTitle} actions={formActions} onBack={goBack} />,
        [formTitle, formActions, goBack]
    );

    return (
        <FormProvider {...methods}>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        {formHeader}
                        <SectionLayout sectionErrors={sectionErrors}>
                            <Section title="correspondence.general" collapsible>
                                <GeneralFields form={form} />
                                <OfficialFields form={form} />
                                <SendersFields form={form} />
                                <NoteFields form={form} />
                                <HandlingFields form={form} />
                            </Section>
                            <Section title="correspondence.connected-client-case" collapsible>
                                <ConnectedClientCaseFields form={form} />
                            </Section>
                            <Section title="correspondence.dms" collapsible>
                                <DmsFields form={form} />
                            </Section>
                        </SectionLayout>
                    </FormWrapper>
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
            <ConfirmNavigation control={control} bypassRef={bypassNavigationBlockerRef} />
        </FormProvider>
    );
};

export default CorrespondencePageForm;
